/**
 * `feature:connection_string_paste` for the dialog's two paste places (Option C, C3, plan §3.7: "parse shown", source
 * `start_filter` or `pane_field`, funnel v2). One event per distinct string that shows a parse: at once for a real paste
 * (or the clipboard read of the Paste connection string entry), after a pause for typing, so a burst of keystrokes
 * collapses to one event carrying the final parse. A pending event is never dropped: Continue / Apply `flush()` it
 * first, and so does unmounting (C3 review C3-07: an Enter right after typing reported the apply without the parse).
 * Only low-cardinality parse facts leave — never the text; only a hash of it is kept, to recognise a repeat.
 */
import { useEffect, useRef } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import { FUNNEL_VERSION } from '../hooks/useConnectionDialogFunnel';

export type PasteSource = 'start_filter' | 'pane_field';

export const PASTE_TRACK_DEBOUNCE_MS = 1500;

/** FNV-1a, enough to tell one string from the previous one without keeping it. */
function textHash(text: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function usePasteTracking(source: PasteSource, track: TrackFunction) {
  const lastTracked = useRef<number | null>(null);
  const timer = useRef<number | null>(null);
  const pending = useRef<{ hash: number; result: ConnectionStringParseResult } | null>(null);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    pending.current = null;
  };

  const fire = (hash: number, result: ConnectionStringParseResult) => {
    cancel();
    lastTracked.current = hash;
    track('feature:connection_string_paste', {
      engine: result.engine ?? 'none',
      // A string several engines read alike asks which (ruling after C3 review r5): not a failure.
      outcome: result.choices ? 'choice' : result.outcome,
      format: result.format,
      source,
      funnel_version: FUNNEL_VERSION,
    });
  };

  /** The text now shows `result` (null: it shows no parse — nothing to track, and a pending event is dropped). */
  const observe = (text: string, result: ConnectionStringParseResult | null, viaPaste: boolean) => {
    cancel();
    const trimmed = text.trim();
    if (!result || !trimmed) return;
    const hash = textHash(trimmed);
    if (hash === lastTracked.current) return;
    if (viaPaste) {
      fire(hash, result);
      return;
    }
    pending.current = { hash, result };
    timer.current = window.setTimeout(() => fire(hash, result), PASTE_TRACK_DEBOUNCE_MS);
  };

  /** Fire a pending (debounced) event now — before Continue / Apply, and on unmount. */
  const flush = () => {
    const waiting = pending.current;
    if (waiting) fire(waiting.hash, waiting.result);
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  return { observe, flush };
}
